import { createContext, useCallback, useContext, useMemo } from 'react';

import type * as models from 'pkg/api/models';
import type { MixedStateSetter } from 'pkg/hooks/useMixedState';

import type { FormFieldV2 } from 'routes/forms/submission/form/interface';

export enum FormSectionOrder {
	EventGroups = 1,
	Fields = 2,
	Agreements = 3,
	// Packages replaces products step (will probably be changed to their own routes in the future)
	ProductsOrPackages = 4,
	PackagePaymentOption = 5,
	Billing = 6,
}

export enum AgreementConsent {
	Accepted = 'accepted',
	Declined = 'declined',
}

export const getProductQtyName = (productId: number) =>
	`product_quantities.productId_${productId}`;

export interface RegistrationFormState {
	preview: models.paymentPreview.PaymentPreview;
	bundlePreviews: models.paymentPreview.PaymentPreview[];
	discount: models.discount.Discount;
	activeSection: FormSectionOrder;
	previousSection: FormSectionOrder;
	activeBundleNumber: number;
	selectedEventGroupIds: number[];
	answerIneligibleEventGroupIds: number[] | null;
	chosenFormEventIds: Record<number, number[]>;
	unavailableFormEventIds: number[];
	selectedFormPackageId: number;
	selectedFormPackagePaymentOptionId: number;
	formFields: FormFieldV2[];
	disableButtons: boolean;
	requiresCaptcha: boolean;
	captchaVerified: boolean;
	isPreview: boolean;
	unlockedFields: Record<string, boolean>;
	agreementConsents: Record<number, AgreementConsent>;
	agreementSignatures: Record<number, string>;
	invitedEventGroupIds?: number[];
	leaveConfirmDropped?: boolean;
	skipRequired?: boolean;
}

export interface RegistrationFormContextProps {
	state: RegistrationFormState;
	setState: MixedStateSetter<RegistrationFormState>;
}

export const RegistrationFormContext =
	createContext<RegistrationFormContextProps>({
		state: {
			preview: null,
			bundlePreviews: [],
			discount: null,
			activeSection: FormSectionOrder.Fields,
			previousSection: null,
			activeBundleNumber: 1,
			selectedEventGroupIds: [],
			answerIneligibleEventGroupIds: null,
			chosenFormEventIds: {},
			unavailableFormEventIds: [],
			selectedFormPackageId: null,
			selectedFormPackagePaymentOptionId: null,
			formFields: [],
			disableButtons: false,
			requiresCaptcha: false,
			captchaVerified: false,
			isPreview: false,
			unlockedFields: {},
			agreementConsents: {},
			agreementSignatures: {},
		},
		setState: () => null,
	});

export function useRegistrationFormContext() {
	return useContext(RegistrationFormContext);
}

export interface FieldUnlock {
	enabled: boolean;
	isUnlocked: boolean;
	toggleUnlock: () => void;
}

export function useFieldUnlock(outputKey: number | string): FieldUnlock {
	const { state, setState } = useRegistrationFormContext();
	const key = outputKey?.toString();

	const isUnlocked = !!state.unlockedFields?.[key];

	const toggleUnlock = useCallback(() => {
		if (!key) {
			return;
		}
		setState({
			unlockedFields: {
				...state.unlockedFields,
				[key]: !state.unlockedFields?.[key],
			},
		});
	}, [key, setState, state.unlockedFields]);

	return useMemo(
		() => ({ enabled: true, isUnlocked, toggleUnlock }),
		[isUnlocked, toggleUnlock]
	);
}
